import { useState } from "react";
import { Monitor } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageHeader,
  TextField,
} from "@nahal/ui";
import { useChangePassword, useRevokeSession, useSessions } from "../../lib/queries";
import { useSession } from "../../lib/session";

export function SecurityScreen() {
  return (
    <>
      <PageHeader title="Security" description="Your password and where you are signed in." />
      <div className="space-y-4">
        <PasswordCard />
        <SessionsCard />
      </div>
    </>
  );
}

function PasswordCard() {
  const change = useChangePassword();
  const clear = useSession((state) => state.clear);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;

  function submit() {
    change.mutate(
      { currentPassword: current, newPassword: next },
      // Changing the password revokes every session, including this one, so the
      // honest thing is to drop straight back to sign-in rather than let the
      // next request fail on a dead refresh token.
      { onSuccess: () => clear() },
    );
  }

  return (
    <Card>
      <CardHeader title="Password" />
      <CardBody className="space-y-4">
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="At least 8 characters."
          error={tooShort ? "At least 8 characters." : undefined}
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          error={mismatch ? "These do not match." : undefined}
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
        <p className="text-xs text-[var(--text-muted)]">
          Changing your password signs out every device, including this one.
        </p>
        <Button
          variant="solid"
          className="w-full sm:w-auto"
          disabled={
            !current || next.length < 8 || next !== confirm || change.isPending
          }
          onClick={submit}
        >
          {change.isPending ? "Changing..." : "Change password"}
        </Button>
      </CardBody>
    </Card>
  );
}

function SessionsCard() {
  const sessions = useSessions();
  const revoke = useRevokeSession();

  return (
    <Card>
      <CardHeader title="Where you are signed in" />
      {sessions.isLoading ? (
        <LoadingBlock />
      ) : (sessions.data?.length ?? 0) === 0 ? (
        <EmptyState icon={<Monitor className="size-5" />} title="No other sessions" />
      ) : (
        <ul className="divide-y divide-[var(--border-subtle)]">
          {sessions.data?.map((session) => (
            <li key={session.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-[var(--text-primary)]">
                  {describe(session.userAgent)}
                </div>
                <div className="mt-0.5 text-xs text-[var(--text-secondary)]">
                  {session.ipAddress ?? "unknown address"} - signed in{" "}
                  {new Date(session.createdAt).toLocaleString()}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(session.id)}
              >
                Sign out
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** A raw user agent is unreadable; the browser and platform are what identify a device to its owner. */
function describe(userAgent: string | null) {
  if (!userAgent) return "Unknown device";
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /Chrome\//.test(userAgent)
      ? "Chrome"
      : /Safari\//.test(userAgent)
        ? "Safari"
        : /Firefox\//.test(userAgent)
          ? "Firefox"
          : "Browser";
  const platform = /Android/.test(userAgent)
    ? "Android"
    : /iPhone|iPad/.test(userAgent)
      ? "iOS"
      : /Windows/.test(userAgent)
        ? "Windows"
        : /Mac OS X/.test(userAgent)
          ? "macOS"
          : /Linux/.test(userAgent)
            ? "Linux"
            : "device";
  return `${browser} on ${platform}`;
}
